import { useEffect, useId, useRef, useState } from 'react';
import { Icon, type IconName } from '../../ui/Icon';
import './setup.css';

/**
 * A row's secondary actions behind one "•••" pressable (Setup pages: zones, map health, team).
 * Like viz.tsx's RowMenu, plus what these rows need: a spoken name per item ("Delete Kamuning"
 * while the menu shows "Delete"), a quiet note under the items (why Remove is off), and the menu
 * closing itself once an item is picked. Same dash.css classes, so it looks and presses the same.
 */
export interface ItemAction {
  label: string;
  ariaLabel?: string;
  icon?: IconName;
  tone?: 'danger';
  disabled?: boolean;
  describedBy?: string;
  onSelect(): void;
}

export function ItemMenu({ label, items, note, noteId }: { label: string; items: ItemAction[]; note?: string | null; noteId?: string }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const id = useId();
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        wrap.current?.querySelector<HTMLElement>('button')?.focus();
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return (
    <div className="dv-pop-wrap" ref={wrap}>
      <button type="button" className="dv-kebab press" aria-label={label} aria-haspopup="menu" aria-expanded={open} aria-controls={open ? id : undefined} onClick={() => setOpen((o) => !o)}>
        <span aria-hidden="true">•••</span>
      </button>
      {open ? (
        <div className="dv-pop st-menu-pop" id={id} role="dialog" aria-label={label}>
          <div className="dv-menu" role="menu">
            {items.map((it) => (
              <button
                key={it.label}
                type="button"
                role="menuitem"
                className={it.tone === 'danger' ? 'danger' : ''}
                disabled={it.disabled}
                aria-label={it.ariaLabel}
                aria-describedby={it.describedBy}
                onClick={() => {
                  setOpen(false);
                  it.onSelect();
                }}
              >
                {it.icon ? <Icon name={it.icon} /> : null}
                {it.label}
              </button>
            ))}
          </div>
          {note ? (
            <p className="st-menu-note" id={noteId}>
              {note}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
